import { useQuery } from "@tanstack/react-query"
import { client, APIError } from "@/api"

interface SubscriptionDetail {
  id: number
  creator_id: number
  entity_id: number | null
  entity_type: string
  organization_public_id: string | null
  organization_name: string | null
  plan_id: string
  plan_version_id: number | null
  stripe_subscription_id: string | null
  stripe_customer_id: string | null
  status: "trialing" | "active" | "cancelled" | "past_due" | "unpaid"
  current_period_start: string | null
  current_period_end: string | null
  trial_end_date: string | null
  cancel_at_period_end: boolean | null
  cancellation_reason: string | null
  cancelled_at: string | null
  /** Non-null means this is a comp: a real Stripe subscription at 100% off. */
  comp_reason: string | null
  created_at: string
  updated_at: string
  creator: {
    public_id: string
    name: string | null
    email: string
  } | null
  team: {
    public_id: string
    name: string
  } | null
  plan: {
    id: string
    name: string
    tier: string
    max_games: number | null
    max_storage_gb: number | null
    max_team_members: number | null
  } | null
  team_subscription: {
    status: string | null
    started_at: string | null
    expires_at: string | null
    cancelled_at: string | null
  } | null
}

interface StripeData {
  id: string
  status: string
  current_period_start: number | null
  current_period_end: number | null
  trial_end: number | null
  cancel_at_period_end: boolean | null
  cancel_at: number | null
  canceled_at: number | null
  /**
   * Whether Stripe is actually applying a 100%-off discount right now, or null
   * when the discounts came back unexpanded and the answer is unknowable.
   */
  is_comp: boolean | null
}

interface StripeCustomer {
  id: string
  email: string | null
  name: string | null | undefined
}

interface Discrepancies {
  status: boolean
  current_period_end: boolean
  cancel_at_period_end: boolean | null
  is_comp: boolean
}

interface DiscrepancyDetails {
  current_period_end?: {
    stripe: number
    database: number
    difference_seconds: number
    difference_minutes: number
  }
  status?: {
    stripe: string
    database: string
  }
  cancel_at_period_end?: {
    stripe: boolean
    database: boolean
  }
  is_comp?: {
    stripe: boolean
    database: boolean
  }
}

interface SubscriptionDetailResponse {
  ok: boolean
  subscription: SubscriptionDetail
  stripe: StripeData | { error: string; details: string } | null
  stripeCustomer: StripeCustomer | null
  discrepancies: Partial<Discrepancies>
  discrepancyDetails: DiscrepancyDetails
  hasDiscrepancies: boolean
}

export function useSubscriptionDetail(subscriptionId: string | number | undefined) {
  return useQuery<SubscriptionDetailResponse>({
    queryKey: ["admin", "subscription", subscriptionId],
    queryFn: async () => {
      if (!subscriptionId) {
        throw new Error("Subscription ID is required")
      }

      const response = await client.admin.subscriptions[":id"].$get({
        param: { id: subscriptionId.toString() },
      })

      if (!response.ok) {
        let errorMessage = `HTTP ${response.status}`
        let errorCode = "SUBSCRIPTION_FETCH_FAILED"

        try {
          const errorData = (await response.clone().json()) as { message?: string; code?: string }
          errorMessage = errorData.message || errorMessage
          errorCode = errorData.code || errorCode
        } catch {
          // Fallback to status text if JSON parsing fails
          errorMessage = response.statusText || errorMessage
        }

        throw new APIError(response.status, errorCode, new Error(errorMessage))
      }

      return response.json()
    },
    enabled: !!subscriptionId,
  })
}
